// src/private/src/pages/app/clients/ZipTargetingAnalyzerPage.jsx
//
// Phase 3 v1: per-client review queue for ZIP filter recommendations.
// Engine + workflow API live in apps.dashboard. Three tabs:
//   - Review Queue: list pending recommendations, per-row + bulk actions
//   - Summary:      counts by recommendation type
//   - Generate:     on-demand engine run trigger
//
// Agency-wide / regional bucket-level rollup tabs are deferred — those need
// dedicated API endpoints on top of services/zip_buckets.aggregate_buckets.

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline as ApproveIcon,
  CancelOutlined as RejectIcon,
  PlayArrowOutlined as ApplyIcon,
  UndoOutlined as RevertIcon,
  RefreshOutlined as RefreshIcon,
  WarningAmberOutlined as ShiftIcon,
} from '@mui/icons-material';

import { fetchClients } from '@src/api/actions/clients';
import {
  approveZipRecommendation,
  applyZipRecommendation,
  bulkApplyZipRecommendations,
  bulkApproveZipRecommendations,
  bulkRejectZipRecommendations,
  fetchZipRecommendations,
  fetchZipRecommendationsSummary,
  generateZipRecommendations,
  rejectZipRecommendation,
  revertZipRecommendation,
} from '@src/api/actions';
import { toast } from '@src/components/core/toaster';

// ── Formatters ──────────────────────────────────────────────────────────────

const fmtUSD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const fmtCount = (n) => Number(n || 0).toLocaleString('en-US');
const fmtPct = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(1)}%`);
const fmtPct1 = (n) => (n == null ? '—' : `${(Number(n) * 100).toFixed(0)}%`);
const fmtRpl = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);
const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—';

// ── Chips ───────────────────────────────────────────────────────────────────

function RecommendationChip({ recommendation }) {
  const map = {
    keep: { label: 'Keep', color: 'success' },
    remove: { label: 'Remove', color: 'error' },
    watch: { label: 'Watch', color: 'warning' },
  };
  const m = map[recommendation] || { label: recommendation, color: 'default' };
  return <Chip size="small" label={m.label} color={m.color} variant="filled" />;
}

function StatusChip({ status }) {
  const map = {
    pending: { label: 'Pending', color: 'warning' },
    approved: { label: 'Approved', color: 'info' },
    applied: { label: 'Applied', color: 'success' },
    rejected: { label: 'Rejected', color: 'default' },
    stale: { label: 'Stale', color: 'default' },
  };
  const m = map[status] || { label: status, color: 'default' };
  return <Chip size="small" label={m.label} color={m.color} variant="outlined" />;
}

function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(100, Number(value || 0) * 100));
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 80 }}>
      <LinearProgress
        variant="determinate"
        value={pct}
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 32 }}>
        {fmtPct1(value)}
      </Typography>
    </Stack>
  );
}

// ── Review Queue Tab ────────────────────────────────────────────────────────

function ReviewQueueTab({ clients }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterClientId, setFilterClientId] = useState('');
  const [filterRecommendation, setFilterRecommendation] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');
  const [selectedIds, setSelectedIds] = useState([]);
  const [actionInFlight, setActionInFlight] = useState(false);

  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    const params = {};
    if (filterClientId) params.client_id = filterClientId;
    if (filterRecommendation) params.recommendation = filterRecommendation;
    if (filterStatus) params.status = filterStatus;
    fetchZipRecommendations(params)
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setRows(Array.isArray(data) ? data : []);
        setSelectedIds([]);
      })
      .catch((err) => setError(err?.message || 'Failed to load recommendations.'))
      .finally(() => setLoading(false));
  }, [filterClientId, filterRecommendation, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const allRowIds = useMemo(() => rows.map((r) => r.id), [rows]);
  const allSelected = allRowIds.length > 0 && allRowIds.every((id) => selectedIds.includes(id));
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleAll = () => setSelectedIds(allSelected ? [] : allRowIds);
  const toggleRow = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const runBulkAction = (label, callable) => {
    if (!selectedIds.length) return;
    setActionInFlight(true);
    callable()
      .then((res) => {
        const updated = res.data?.updated_count ?? 0;
        toast.success(`${label}: ${updated} of ${selectedIds.length} updated`);
        setSelectedIds([]);
        load();
      })
      .catch((err) => toast.error(err?.response?.data?.detail || err?.message || `${label} failed`))
      .finally(() => setActionInFlight(false));
  };

  const handleBulkApprove = () =>
    runBulkAction('Approve', () => bulkApproveZipRecommendations({ ids: selectedIds, apply: false }));

  const handleBulkApproveAndApply = () =>
    runBulkAction('Approve + Apply', () => bulkApproveZipRecommendations({ ids: selectedIds, apply: true }));

  const handleBulkApply = () =>
    runBulkAction('Apply', () => bulkApplyZipRecommendations({ ids: selectedIds }));

  const handleBulkReject = () => {
    if (!selectedIds.length) return;
    const reason = window.prompt('Reject reason (optional)?') || '';
    runBulkAction('Reject', () => bulkRejectZipRecommendations({ ids: selectedIds, reason }));
  };

  // Per-row actions

  const runRowAction = (label, callable) => {
    setActionInFlight(true);
    callable()
      .then(() => {
        toast.success(label);
        load();
      })
      .catch((err) => toast.error(err?.response?.data?.detail || err?.message || `${label} failed`))
      .finally(() => setActionInFlight(false));
  };

  const onApprove = (row) => runRowAction('Approved', () => approveZipRecommendation(row.id));
  const onApproveAndApply = (row) =>
    runRowAction('Approved + Applied', () => approveZipRecommendation(row.id, { apply: true }));
  const onApply = (row) => runRowAction('Applied', () => applyZipRecommendation(row.id));
  const onRevert = (row) => runRowAction('Reverted', () => revertZipRecommendation(row.id));

  const openReject = (row) => {
    setRejectTarget(row);
    setRejectReason('');
  };
  const submitReject = () => {
    if (!rejectTarget) return;
    runRowAction('Rejected', () => rejectZipRecommendation(rejectTarget.id, rejectReason));
    setRejectTarget(null);
    setRejectReason('');
  };

  return (
    <Fragment>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
            <Autocomplete
              size="small"
              sx={{ minWidth: 240 }}
              options={clients}
              getOptionLabel={(c) => c.name || c.client_name || `Client ${c.id}`}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              value={clients.find((c) => String(c.id) === String(filterClientId)) || null}
              onChange={(_, val) => setFilterClientId(val ? val.id : '')}
              renderInput={(params) => <TextField {...params} label="Client" />}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Recommendation</InputLabel>
              <Select
                label="Recommendation"
                value={filterRecommendation}
                onChange={(e) => setFilterRecommendation(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="remove">Remove</MenuItem>
                <MenuItem value="keep">Keep</MenuItem>
                <MenuItem value="watch">Watch</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="applied">Applied</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
                <MenuItem value="stale">Stale</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </FormControl>
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh">
              <span>
                <IconButton onClick={load} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {selectedIds.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mt: 2 }} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                {selectedIds.length} selected
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="small"
                startIcon={<ApproveIcon />}
                disabled={actionInFlight}
                onClick={handleBulkApprove}
              >
                Approve
              </Button>
              <Button
                size="small"
                variant="contained"
                startIcon={<ApplyIcon />}
                disabled={actionInFlight}
                onClick={handleBulkApproveAndApply}
              >
                Approve + Apply
              </Button>
              <Button
                size="small"
                startIcon={<ApplyIcon />}
                disabled={actionInFlight}
                onClick={handleBulkApply}
              >
                Apply (already approved)
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<RejectIcon />}
                disabled={actionInFlight}
                onClick={handleBulkReject}
              >
                Reject
              </Button>
            </Stack>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      indeterminate={someSelected}
                      checked={allSelected}
                      onChange={toggleAll}
                      disabled={!rows.length}
                    />
                  </TableCell>
                  <TableCell>Recommendation</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Client / Vertical / Territory</TableCell>
                  <TableCell>ZIP</TableCell>
                  <TableCell>City, State</TableCell>
                  <TableCell align="right">Income</TableCell>
                  <TableCell align="right">Home Value</TableCell>
                  <TableCell align="right">Population</TableCell>
                  <TableCell align="right">RPL (obs / base)</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="right">Audience Δ</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && !rows.length && (
                  <TableRow>
                    <TableCell colSpan={14} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No recommendations match the current filters.</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  rows.map((row) => {
                    const observed = row.observed_metrics || {};
                    const baseline = row.baseline_metrics || {};
                    const audDelta =
                      row.audience_size_after != null && row.audience_size_before != null
                        ? row.audience_size_after - row.audience_size_before
                        : null;
                    const isSelectable = row.status === 'pending' || row.status === 'approved' || row.status === 'applied';
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedIds.includes(row.id)}
                            onChange={() => toggleRow(row.id)}
                            disabled={!isSelectable}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center">
                            <RecommendationChip recommendation={row.recommendation} />
                            {row.recent_shift_flagged && (
                              <Tooltip title="Recent agency-wide shift detected for this bucket — review carefully">
                                <ShiftIcon fontSize="small" color="warning" />
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <StatusChip status={row.status} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{row.client_id} / {row.vertical__vertical || row.vertical_id}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {row.client_territory__name || `Territory ${row.client_territory_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontFamily="monospace">{row.zip_code}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {row.zip_code_data__city || '—'}
                            {row.zip_code_data__state_full ? `, ${row.zip_code_data__state_full}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {row.zip_code_data__household_income ? fmtUSD.format(row.zip_code_data__household_income) : '—'}
                        </TableCell>
                        <TableCell align="right">
                          {row.zip_code_data__home_value ? fmtUSD.format(row.zip_code_data__home_value) : '—'}
                        </TableCell>
                        <TableCell align="right">{fmtCount(row.population_zip)}</TableCell>
                        <TableCell align="right">
                          <Tooltip
                            title={
                              <span>
                                Observed RPL: {fmtRpl(observed.blended_rpl)}<br />
                                Baseline RPL: {fmtRpl(baseline.blended_rpl)}<br />
                                Observed close: {fmtPct(observed.blended_close_rate)}<br />
                                Bucket: {observed.bucket || '—'}<br />
                                n leads (cohort): {fmtCount(observed.n_leads)}
                              </span>
                            }
                          >
                            <Box>
                              <Typography variant="body2">{fmtRpl(observed.blended_rpl)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                vs {fmtRpl(baseline.blended_rpl)}
                              </Typography>
                            </Box>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ minWidth: 120 }}>
                          <ConfidenceBar value={row.confidence} />
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {row.reason?.replace(/_/g, ' ')}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {audDelta != null ? (
                            <Tooltip title={`Before: ${fmtCount(row.audience_size_before)}  →  After: ${fmtCount(row.audience_size_after)}`}>
                              <Typography
                                variant="body2"
                                color={audDelta < 0 ? 'error.main' : 'text.secondary'}
                              >
                                {audDelta < 0 ? '−' : '+'}
                                {fmtCount(Math.abs(audDelta))}
                              </Typography>
                            </Tooltip>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            {row.status === 'pending' && (
                              <Fragment>
                                <Tooltip title="Approve">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      disabled={actionInFlight}
                                      onClick={() => onApprove(row)}
                                    >
                                      <ApproveIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Approve + Apply">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={actionInFlight}
                                      onClick={() => onApproveAndApply(row)}
                                    >
                                      <ApplyIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={actionInFlight}
                                      onClick={() => openReject(row)}
                                    >
                                      <RejectIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Fragment>
                            )}
                            {row.status === 'approved' && (
                              <Fragment>
                                <Tooltip title="Apply">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      disabled={actionInFlight}
                                      onClick={() => onApply(row)}
                                    >
                                      <ApplyIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      disabled={actionInFlight}
                                      onClick={() => openReject(row)}
                                    >
                                      <RejectIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Fragment>
                            )}
                            {row.status === 'applied' && (
                              <Tooltip title="Revert (un-flip ZIP filter)">
                                <span>
                                  <IconButton
                                    size="small"
                                    disabled={actionInFlight}
                                    onClick={() => onRevert(row)}
                                  >
                                    <RevertIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={!!rejectTarget} onClose={() => setRejectTarget(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reject recommendation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            ZIP {rejectTarget?.zip_code} — {rejectTarget?.recommendation}
          </Typography>
          <TextField
            label="Reason (optional)"
            multiline
            minRows={3}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={submitReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}

// ── Summary Tab ─────────────────────────────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: 1, minWidth: 160 }}>
      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" sx={{ mt: 0.5, color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Paper>
  );
}

function SummaryTab({ clients }) {
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [filterClientId, setFilterClientId] = useState('');
  const [filterStatus, setFilterStatus] = useState('pending');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (filterClientId) params.client_id = filterClientId;
    if (filterStatus) params.status = filterStatus;
    fetchZipRecommendationsSummary(params)
      .then((res) => setCounts(res.data || {}))
      .catch((err) => toast.error(err?.message || 'Failed to load summary.'))
      .finally(() => setLoading(false));
  }, [filterClientId, filterStatus]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Card>
      <CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start" sx={{ mb: 3 }}>
          <Autocomplete
            size="small"
            sx={{ minWidth: 240 }}
            options={clients}
            getOptionLabel={(c) => c.name || c.client_name || `Client ${c.id}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={clients.find((c) => String(c.id) === String(filterClientId)) || null}
            onChange={(_, val) => setFilterClientId(val ? val.id : '')}
            renderInput={(params) => <TextField {...params} label="Client (optional)" />}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="approved">Approved</MenuItem>
              <MenuItem value="applied">Applied</MenuItem>
              <MenuItem value="rejected">Rejected</MenuItem>
              <MenuItem value="stale">Stale</MenuItem>
              <MenuItem value="all">All statuses</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ flex: 1 }} />
          <Tooltip title="Refresh">
            <span>
              <IconButton onClick={load} disabled={loading}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <StatCard label="Keep" value={fmtCount(counts.keep)} color="success.main" />
            <StatCard label="Remove" value={fmtCount(counts.remove)} color="error.main" />
            <StatCard label="Watch" value={fmtCount(counts.watch)} color="warning.main" />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

// ── Generate Tab ────────────────────────────────────────────────────────────

function GenerateTab({ clients }) {
  const [clientId, setClientId] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);

  const trigger = () => {
    if (!clientId) return;
    setBusy(true);
    generateZipRecommendations(clientId, { endDate: endDate || null, runAsync: true })
      .then(() => toast.success('Run queued — check the Review Queue once Celery completes.'))
      .catch((err) => toast.error(err?.response?.data?.detail || err?.message || 'Failed to queue run'))
      .finally(() => setBusy(false));
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Trigger an on-demand recommendation run for a single client. The engine will read the
          last 30/60/90/120/180 days of ZipSummary data, compute bucket-level scores, and write
          new PENDING recommendations (superseding any old PENDINGs from prior runs).
        </Typography>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
          <Autocomplete
            size="small"
            sx={{ minWidth: 280 }}
            options={clients}
            getOptionLabel={(c) => c.name || c.client_name || `Client ${c.id}`}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            value={clients.find((c) => String(c.id) === String(clientId)) || null}
            onChange={(_, val) => setClientId(val ? val.id : '')}
            renderInput={(params) => <TextField {...params} label="Client" required />}
          />
          <TextField
            size="small"
            label="End date (optional)"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            disabled={!clientId || busy}
            onClick={trigger}
          >
            {busy ? 'Queueing…' : 'Generate Recommendations'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

export default function ZipTargetingAnalyzerPage() {
  const [activeTab, setActiveTab] = useState(0);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(true);

  useEffect(() => {
    fetchClients()
      .then((res) => {
        const data = res.data?.results || res.data || [];
        setClients(Array.isArray(data) ? data : []);
      })
      .catch(() => setClients([]))
      .finally(() => setClientsLoading(false));
  }, []);

  return (
    <Box>
      <Stack spacing={3}>
        <Card>
          <CardHeader
            title="ZIP Targeting Analyzer"
            subheader="Bucket-level recommendation engine: review keep/remove/watch decisions per ZIP, approve and apply to ClientZipVertical."
          />
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Review Queue" />
            <Tab label="Summary" />
            <Tab label="Generate" />
          </Tabs>
        </Card>
        {clientsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Fragment>
            {activeTab === 0 && <ReviewQueueTab clients={clients} />}
            {activeTab === 1 && <SummaryTab clients={clients} />}
            {activeTab === 2 && <GenerateTab clients={clients} />}
          </Fragment>
        )}
      </Stack>
    </Box>
  );
}
